import { AdminFeature } from './types';

/**
 * Feature Registry
 *
 * Singleton registry for managing admin features.
 * Features can register themselves at application startup.
 */
export class FeatureRegistry {
  private static instance: FeatureRegistry;
  private features: Map<string, AdminFeature> = new Map();

  private constructor() {}

  /**
   * Get the singleton instance
   */
  static getInstance(): FeatureRegistry {
    if (!FeatureRegistry.instance) {
      FeatureRegistry.instance = new FeatureRegistry();
    }
    return FeatureRegistry.instance;
  }

  /**
   * Register a new feature
   */
  register(feature: AdminFeature): void {
    if (this.features.has(feature.id)) {
      console.warn(`Feature with id "${feature.id}" is already registered. Overwriting.`);
    }
    this.features.set(feature.id, feature);
  }

  /**
   * Get all registered features, sorted by order
   */
  getFeatures(): AdminFeature[] {
    return Array.from(this.features.values()).sort((a, b) => {
      const orderA = a.order ?? 999;
      const orderB = b.order ?? 999;
      return orderA - orderB;
    });
  }

  /**
   * Get features visible in navigation
   */
  getNavigationFeatures(): AdminFeature[] {
    return this.getFeatures().filter(feature => !feature.hideFromNav);
  }

  /**
   * Get a specific feature by id
   */
  getFeature(id: string): AdminFeature | undefined {
    return this.features.get(id);
  }

  /**
   * Clear all registered features (useful for testing)
   */
  clear(): void {
    this.features.clear();
  }
}

/**
 * Helper function to get the registry instance
 */
export const getFeatureRegistry = () => FeatureRegistry.getInstance();
